import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import ActivityHistoryTimeLine from "../activity/ActivityHistoryTimeLine";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import {
  useActivityFilters,
  getDefaultContainerHistoryPeriod,
} from "../activity/hooks/useActivityFilters";
import { useActivityPageFilterOptions } from "../activity/hooks/useActivityPageFilterOptions";
import {
  buildKeywordFilterValue,
  syncKeywordFiltersFromValue,
} from "../activity/utils/activityPageFilters";
import StatisticsInlineFilters from "../statistics/components/StatisticsInlineFilters";

const ActivityHistory = () => {
  const { t } = useTranslation();
  const token = Cookies.get("userToken");

  const {
    keywordFilter,
    setKeywordFilter,
    periodHistoryStart,
    setPeriodHistoryStart,
    periodHistoryStop,
    setPeriodHistoryStop,
    periodFilter,
    setPeriodFilter,
    periodName,
    setPeriodName,
  } = useActivityFilters({
    storageKey: "containerHistoryFilters",
    getDefaultPeriod: getDefaultContainerHistoryPeriod,
    defaultPeriodNameKey: "Last 30 days",
  });

  const { groupedOptions } = useActivityPageFilterOptions();

  const [handleValidate, setHandleValidate] = useState(0);
  const [fluxFilter, setFluxFilter] = useState("all");
  const [addressFilter, setAddressFilter] = useState("all");
  const [zoneFilter, setZoneFilter] = useState("all");
  const hasInitializedKeywordFilters = useRef(false);

  const withAllOption = useCallback(
    (options) => [{ label: t("All"), value: "all" }, ...options],
    [t],
  );

  const fluxOptions = useMemo(
    () => withAllOption(groupedOptions.flux),
    [groupedOptions.flux, withAllOption],
  );
  const addressOptions = useMemo(
    () => withAllOption(groupedOptions.address),
    [groupedOptions.address, withAllOption],
  );
  const zoneOptions = useMemo(
    () => withAllOption(groupedOptions.zone),
    [groupedOptions.zone, withAllOption],
  );

  useEffect(() => {
    if (hasInitializedKeywordFilters.current || !keywordFilter?.value?.length) {
      return;
    }

    const synced = syncKeywordFiltersFromValue(keywordFilter);
    setZoneFilter(synced.zoneFilter);
    setFluxFilter(synced.fluxFilter);
    setAddressFilter(synced.addressFilter);
    hasInitializedKeywordFilters.current = true;
  }, [keywordFilter]);

  const applyKeywordFilters = useCallback(
    (patch) => {
      const next = {
        zoneFilter,
        fluxFilter,
        addressFilter,
        ...patch,
      };

      if (patch.zoneFilter !== undefined) setZoneFilter(patch.zoneFilter);
      if (patch.fluxFilter !== undefined) setFluxFilter(patch.fluxFilter);
      if (patch.addressFilter !== undefined) {
        setAddressFilter(patch.addressFilter);
      }

      setKeywordFilter(
        buildKeywordFilterValue({
          ...next,
          optionsByValue: groupedOptions.byValue,
        }),
      );
      setHandleValidate((previous) => previous + 1);
    },
    [
      zoneFilter,
      fluxFilter,
      addressFilter,
      groupedOptions.byValue,
      setKeywordFilter,
    ],
  );

  const handleClearFilters = useCallback(() => {
    const defaultPeriod = getDefaultContainerHistoryPeriod();
    setZoneFilter("all");
    setFluxFilter("all");
    setAddressFilter("all");
    setKeywordFilter({ value: [] });
    setPeriodFilter(defaultPeriod.periodFilter);
    setPeriodHistoryStart(defaultPeriod.periodHistoryStart);
    setPeriodHistoryStop(defaultPeriod.periodHistoryStop);
    setPeriodName(t("Last 30 days"));
    setHandleValidate((previous) => previous + 1);
  }, [
    setKeywordFilter,
    setPeriodFilter,
    setPeriodHistoryStart,
    setPeriodHistoryStop,
    setPeriodName,
    t,
  ]);

  if (!token) {
    return null;
  }

  return (
    <div className="w-full">
      <StatisticsInlineFilters
        periodDisplayLabel={periodName}
        periodFilter={periodFilter}
        periodHistoryStart={periodHistoryStart}
        periodHistoryStop={periodHistoryStop}
        onApplyPeriod={(months, label, startDateIso, endDateIso, presetId) => {
          setPeriodFilter(presetId === "sinceBeginning" ? null : months);
          setPeriodName(label || t("Last 30 days"));
          if (startDateIso) setPeriodHistoryStart(startDateIso);
          if (endDateIso) setPeriodHistoryStop(endDateIso);
          setHandleValidate((previous) => previous + 1);
        }}
        fluxOptions={fluxOptions}
        fluxFilter={fluxFilter}
        onApplyFlux={(value) => applyKeywordFilters({ fluxFilter: value })}
        addressOptions={addressOptions}
        addressFilter={addressFilter}
        onApplyAddress={(value) =>
          applyKeywordFilters({ addressFilter: value })
        }
        zoneOptions={zoneOptions}
        zoneFilter={zoneFilter}
        onApplyZone={(value) => applyKeywordFilters({ zoneFilter: value })}
        showZoneFilter
        onClearFilters={handleClearFilters}
      />

      <ActivityHistoryTimeLine
        keywordFilter={keywordFilter}
        periodHistoryStart={periodHistoryStart}
        periodHistoryStop={periodHistoryStop}
        handleValidate={handleValidate}
        periodName={periodName}
        hasActiveFilters={(keywordFilter?.value?.length ?? 0) > 0}
        onClearFilters={handleClearFilters}
      />
    </div>
  );
};

export default ActivityHistory;
